import styled from "styled-components";
import { SubgridBodyColumn, SubgridColumn, SubgridSortedBy } from "./store/types";
import { Grid } from "../Box/Box";
import { SubgridHead } from "./components/SubgridHead";
import React, { useMemo, useRef, useState } from "react";
import { reduceSubgridTableColumnsToBodyColumns } from "./store/utils";
import { SubgridBody } from "./components/SubgridBody";
import { SubgridFoot } from "./components/SubgridFoot";
import { Scroller } from "../Scroller/Scroller";

const TableElement = styled(Grid)<{ bodyColumns: SubgridBodyColumn<object>[] }>`
  width: 100%;
  grid-template-columns: ${({ bodyColumns }) =>
    bodyColumns.map((bodyColumn) => bodyColumn.width ?? "auto").join(" ")};
`;

interface Props<T> {
  columns: SubgridColumn<T>[];
  data: T[];
  align?: "center";
  virtualRowHeight?: number;
  rowRenderer?: (row: T) => React.ReactNode;
  isSortable?: boolean;
  page?: number;
  rowsPerPage?: number;
  headersNoWrap?: boolean;
  rowBackground?: (row: T) => string | undefined;
}

export const SubgridTable = <T extends object>({
  columns,
  data,
  align,
  virtualRowHeight,
  rowRenderer,
  isSortable = false,
  page,
  rowsPerPage,
  headersNoWrap,
  rowBackground,
}: Props<T>) => {
  const refScroller = useRef<HTMLDivElement>(null);

  const [sortedBy, setSortedBy] = useState<SubgridSortedBy>(null);

  const sortedData = useMemo(() => {
    if (!sortedBy) {
      return data;
    }

    return [...data].sort((dataRow1, dataRow2) => {
      const typedDataRow1 = dataRow1 as Record<
        string,
        string | number | boolean | null | undefined
      >;
      const typedDataRow2 = dataRow2 as Record<
        string,
        string | number | boolean | null | undefined
      >;
      const dataRow1Value = typedDataRow1[sortedBy.id];
      const dataRow2Value = typedDataRow2[sortedBy.id];
      const direction = sortedBy.direction;

      if (!dataRow1Value) {
        return 1;
      }

      if (!dataRow2Value) {
        return -1;
      }

      if (direction === "descending") {
        return dataRow1Value > dataRow2Value ? -1 : 1;
      }

      if (direction === "ascending") {
        return dataRow1Value < dataRow2Value ? -1 : 1;
      }

      return 0;
    });
  }, [data, sortedBy]);

  const filteredData = useMemo(() => {
    if (page === undefined || rowsPerPage === undefined) {
      return sortedData;
    }

    return sortedData.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const bodyColumns = useMemo(() => {
    return reduceSubgridTableColumnsToBodyColumns(columns);
  }, [columns]);

  return (
    <Scroller ref={refScroller} gutter="auto">
      <TableElement bodyColumns={bodyColumns}>
        <SubgridHead
          columns={columns}
          isSortable={isSortable}
          sortedBy={sortedBy}
          setSortedBy={setSortedBy}
          headersNoWrap={headersNoWrap}
        />

        <SubgridBody
          bodyColumns={bodyColumns}
          data={filteredData}
          align={align}
          virtualRowHeight={virtualRowHeight}
          rowRenderer={rowRenderer}
          rowBackground={rowBackground}
          refScroller={refScroller}
        />

        {columns.find((column) => column.footer !== undefined) && <SubgridFoot columns={columns} />}
      </TableElement>
    </Scroller>
  );
};
